import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle2, XCircle, Clock, MessageSquare } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface PostApproval {
  id: string;
  post_id: string;
  workflow_id: string | null;
  approver_id: string;
  status: string;
  comments: string | null;
  created_at: string;
  approved_at: string | null;
  posts: {
    content: string;
    platform: string;
    scheduled_for: string | null;
  };
}

export const ApprovalsView = ({ showHeader = true }: { showHeader?: boolean }) => {
  const [approvals, setApprovals] = useState<PostApproval[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pending");
  const [selectedApproval, setSelectedApproval] = useState<PostApproval | null>(null);
  const [comments, setComments] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<{ approvalId: string; postId: string; workflowId: string | null } | null>(null);
  const [rejectFeedback, setRejectFeedback] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    fetchApprovals();
    
    // Subscribe to real-time updates
    const channel = supabase
      .channel('approvals-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'post_approvals'
        },
        () => {
          fetchApprovals();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchApprovals = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('post_approvals')
        .select('*')
        .eq('approver_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch post details separately
      const approvalsWithPosts = await Promise.all(
        (data || []).map(async (approval) => {
          const { data: post } = await supabase
            .from('posts')
            .select('content, scheduled_for, social_account_id')
            .eq('id', approval.post_id)
            .single();

          let platform = 'unknown';
          if (post?.social_account_id) {
            const { data: account } = await supabase
              .from('social_accounts')
              .select('platform')
              .eq('id', post.social_account_id)
              .single();
            platform = account?.platform || 'unknown';
          }

          return {
            ...approval,
            posts: {
              content: post?.content || '',
              platform,
              scheduled_for: post?.scheduled_for || null,
            }
          };
        })
      );

      setApprovals(approvalsWithPosts);
    } catch (error) {
      console.error("Error fetching approvals:", error);
      toast({
        title: "Error",
        description: "Failed to load approvals",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApprovalAction = async (approvalId: string, status: 'approved' | 'rejected') => {
    setActionLoading(true);
    try {
      const approval = approvals.find(a => a.id === approvalId);

      const { error } = await supabase
        .from('post_approvals')
        .update({
          status,
          approved_at: new Date().toISOString(),
          comments: comments || null,
        })
        .eq('id', approvalId);

      if (error) throw error;

      // On approve: advance to next approver (chain mode or static steps)
      if (status === 'approved' && approval?.workflow_id) {
        const { data: workflow } = await supabase
          .from('approval_workflows')
          .select('steps, chain_mode, org_id')
          .eq('id', approval.workflow_id)
          .single();

        if ((workflow as any)?.chain_mode) {
          // Walk up the organogram from the current approver
          const { data: nextId } = await (supabase as any).rpc('next_approver_in_chain', {
            p_org_id:  (workflow as any).org_id,
            p_user_id: approval.approver_id,
          });
          if (nextId) {
            await supabase.from('post_approvals').insert({
              post_id:     approval.post_id,
              workflow_id: approval.workflow_id,
              approver_id: nextId,
              status:      'pending',
            } as any);
            await supabase.functions.invoke('notify-approver', {
              body: { post_id: approval.post_id, workflow_id: approval.workflow_id, approver_id: nextId }
            });
          } else {
            // Top of chain reached — mark post approved
            await supabase.from('posts').update({ status: 'approved' } as any).eq('id', approval.post_id);
          }
        } else {
          // Static steps: find and notify the next step
          const steps = ((workflow as any)?.steps as Array<{ approver_id?: string }>) || [];
          const currentIdx = steps.findIndex((s) => s.approver_id === approval.approver_id);
          const nextStep = steps[currentIdx + 1];
          if (nextStep?.approver_id) {
            await supabase.functions.invoke('notify-approver', {
              body: {
                post_id:     approval.post_id,
                workflow_id: approval.workflow_id,
                step_index:  currentIdx + 1,
                approver_id: nextStep.approver_id,
              }
            });
          } else if (currentIdx === steps.length - 1) {
            // Last step approved — mark post approved
            await supabase.from('posts').update({ status: 'approved' } as any).eq('id', approval.post_id);
          }
        }
      }

      toast({
        title: status === 'approved' ? "Post Approved" : "Post Rejected",
        description: `The post has been ${status}`,
      });

      setSelectedApproval(null);
      setComments("");
      fetchApprovals();
    } catch (error) {
      console.error("Error updating approval:", error);
      toast({
        title: "Error",
        description: "Failed to update approval status",
        variant: "destructive",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectWithFeedback = async () => {
    if (!rejectTarget || !rejectFeedback.trim()) return;
    setActionLoading(true);
    try {
      const { error: approvalError } = await supabase.from('post_approvals')
        .update({
          status:      'rejected',
          comments:    rejectFeedback,
          approved_at: new Date().toISOString(),
        })
        .eq('id', rejectTarget.approvalId);
      if (approvalError) throw approvalError;

      const { error: postError } = await supabase.from('posts')
        .update({ status: 'rejected' as any, error_message: `Rejected: ${rejectFeedback}` })
        .eq('id', rejectTarget.postId);
      if (postError) throw postError;

      const { data: post } = await supabase
        .from('posts')
        .select('user_id, social_account_id')
        .eq('id', rejectTarget.postId)
        .single();

      if (post?.user_id) {
        // Best-effort: a missing notification shouldn't undo an already-recorded rejection.
        await supabase.from('notifications').insert({
          user_id: post.user_id,
          title:   'Post rejected',
          message: `Your post was rejected. Feedback: ${rejectFeedback}`,
          type:    'info',
          is_read: false,
        });
      }

      toast({ title: 'Post Rejected', description: 'Feedback sent to the author.' });
      setRejectDialogOpen(false);
      setRejectTarget(null);
      setRejectFeedback('');
      setSelectedApproval(null);
      fetchApprovals();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to reject post', variant: 'destructive' });
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'rejected':
        return <XCircle className="w-4 h-4 text-destructive" />;
      default:
        return <Clock className="w-4 h-4 text-amber-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive"> = {
      approved: "default",
      pending: "secondary",
      rejected: "destructive",
    };
    return <Badge variant={variants[status] || "secondary"}>{status}</Badge>;
  };

  const filteredApprovals = approvals.filter(approval => approval.status === activeTab);

  return (
    <div className="space-y-6">
      {showHeader && (
        <div>
          <h2 className="text-2xl font-bold mb-2">Post Approvals</h2>
          <p className="text-muted-foreground">Review and approve pending posts</p>
        </div>
      )}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pending">
            Pending ({approvals.filter(a => a.status === 'pending').length})
          </TabsTrigger>
          <TabsTrigger value="approved">
            Approved ({approvals.filter(a => a.status === 'approved').length})
          </TabsTrigger>
          <TabsTrigger value="rejected">
            Rejected ({approvals.filter(a => a.status === 'rejected').length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="space-y-4">
          {loading ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                Loading approvals...
              </CardContent>
            </Card>
          ) : filteredApprovals.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Clock className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">No {activeTab} approvals</h3>
                <p className="text-justify text-muted-foreground">
                  {activeTab === 'pending' 
                    ? "All caught up! No posts waiting for your approval."
                    : `No ${activeTab} posts to show.`}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {filteredApprovals.map((approval) => (
                <Card key={approval.id} className="hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {getStatusIcon(approval.status)}
                          {getStatusBadge(approval.status)}
                          <Badge variant="outline">{approval.posts.platform}</Badge>
                        </div>
                        <CardDescription>
                          Submitted {new Date(approval.created_at).toLocaleString()}
                        </CardDescription>
                      </div>
                      {approval.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedApproval(approval);
                              setComments("");
                            }}
                          >
                            Review
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs text-muted-foreground">Post Content</Label>
                        <p className="text-justify text-sm mt-1 line-clamp-3">{approval.posts.content}</p>
                      </div>
                      {approval.posts.scheduled_for && (
                        <div>
                          <Label className="text-xs text-muted-foreground">Scheduled For</Label>
                          <p className="text-justify text-sm mt-1">
                            {new Date(approval.posts.scheduled_for).toLocaleString()}
                          </p>
                        </div>
                      )}
                      {approval.comments && (
                        <div className="flex items-start gap-2 p-3 bg-muted rounded-lg">
                          <MessageSquare className="w-4 h-4 mt-0.5 text-muted-foreground" />
                          <div className="flex-1">
                            <Label className="text-xs text-muted-foreground">Comments</Label>
                            <p className="text-justify text-sm mt-1">{approval.comments}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Review Dialog */}
      <Dialog open={!!selectedApproval} onOpenChange={(open) => !open && setSelectedApproval(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review Post</DialogTitle>
            <DialogDescription>
              Approve or reject this post with optional comments
            </DialogDescription>
          </DialogHeader>
          {selectedApproval && (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <Label className="text-xs text-muted-foreground">Post Content</Label>
                <p className="text-justify text-sm mt-2">{selectedApproval.posts.content}</p>
              </div>
              <div>
                <Label htmlFor="comments">Comments (Optional)</Label>
                <Textarea
                  id="comments"
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder="Add feedback or notes..."
                  rows={3}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setSelectedApproval(null)}
                  disabled={actionLoading}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    setRejectTarget({
                      approvalId: selectedApproval.id,
                      postId:     selectedApproval.post_id,
                      workflowId: selectedApproval.workflow_id,
                    });
                    setRejectFeedback('');
                    setRejectDialogOpen(true);
                  }}
                  disabled={actionLoading}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
                <Button
                  onClick={() => handleApprovalAction(selectedApproval.id, 'approved')}
                  disabled={actionLoading}
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Approve
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Rejection feedback dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={o => !o && setRejectDialogOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Post</DialogTitle>
            <DialogDescription>
              Provide feedback explaining why this post needs revision.
              The author will receive this feedback immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <Textarea
              value={rejectFeedback}
              onChange={e => setRejectFeedback(e.target.value)}
              placeholder="Explain what needs to be changed…"
              className="min-h-[80px]"
            />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setRejectDialogOpen(false)} disabled={actionLoading}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={!rejectFeedback.trim() || actionLoading}
                onClick={handleRejectWithFeedback}
              >
                Reject with Feedback
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
